import React from 'react';
import { motion } from 'framer-motion';
import { X, Check, Lock, Zap, AlertTriangle, ShieldCheck } from 'lucide-react';
interface ExitUpsellPopupProps {
  onAccept: () => void;
  onDecline: () => void;
  onClose: () => void;
}
const pulseAnimation = {
  scale: [1, 1.03, 1],
  transition: {
    repeat: Infinity,
    duration: 2
  }
};
const flashAnimation = {
  opacity: [1, 0.5, 1],
  transition: {
    repeat: Infinity,
    duration: 1,
    ease: 'easeInOut'
  }
};
export function ExitUpsellPopup({
  onAccept,
  onDecline,
  onClose
}: ExitUpsellPopupProps) {
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.2
  }} className="fixed inset-0 z-[100] flex items-center justify-center p-4" onClick={onClose} data-id="element-1">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" data-id="element-2" />

      {/* Modal */}
      <motion.div initial={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} transition={{
      duration: 0.25,
      ease: 'easeOut'
    }} className="relative w-full max-w-2xl max-h-[90vh] bg-[#111] border border-[#f5a623]/30 rounded-2xl shadow-[0_0_60px_rgba(245,166,35,0.15)] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-3">
        {/* Close Button */}
        <button onClick={onClose} className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-white/5 hover:bg-white/10 transition-colors text-gray-400 hover:text-white z-10" data-id="element-4">
          <X size={16} data-id="element-5" />
        </button>

        <div className="overflow-y-auto w-full" data-id="element-6">
          {/* Header Banner */}
          <div className="bg-[#1a1a1a] border-b border-white/10 p-6 text-center pt-8" data-id="element-7">
            <motion.div animate={flashAnimation} className="inline-flex items-center gap-2 bg-[#ff3d3d]/10 text-[#ff3d3d] border border-[#ff3d3d]/30 px-3 py-1 rounded-full text-xs font-bold tracking-wider uppercase mb-4" data-id="element-8">
              <Zap size={14} data-id="element-9" />
              OFERTA ÚNICA EN PANTALLA
            </motion.div>
            <h2 className="text-2xl md:text-3xl font-black text-white mb-2 leading-tight" data-id="element-10">
              🚨 ¡ESPERA! No te vayas sin esto...
            </h2>
            <p className="text-[#f5a623] text-sm md:text-base font-medium" data-id="element-11">
              ¿Sabías que el 87% de los que compraron el paquete completo
              generaron resultados 3x más rápido?
            </p>
          </div>

          {/* Body Content */}
          <div className="p-6 md:p-8 space-y-6" data-id="element-12">
            <div className="text-center" data-id="element-13">
              <motion.div animate={flashAnimation} className="inline-block bg-[#f5a623] text-black font-black px-4 py-1.5 rounded-lg text-lg mb-4 transform -rotate-2" data-id="element-14">
                🔥 30% DE DESCUENTO EN LOS EXTRAS
              </motion.div>
              <p className="text-gray-300 text-sm md:text-base leading-relaxed mb-2" data-id="element-15">
                Estás a punto de dejar sobre la mesa las herramientas que
                convierten tu cuenta invisible en una máquina de ingresos en
                dólares. Con los Nichos de Oro y los Scripts de Venta, cada
                video que publiques estará diseñado para maximizar tu CPM y
                convertir espectadores en compradores.
              </p>
              <p className="text-white font-semibold text-sm md:text-base" data-id="element-16">
                Esto es lo que te llevas con el paquete completo:
              </p>
            </div>

            {/* What's Included List */}
            <div className="bg-[#1a1a1a] rounded-xl border border-white/5 p-5 space-y-4" data-id="element-17">
              <div className="flex items-start gap-3" data-id="element-18">
                <Check className="text-[#00e676] shrink-0 mt-0.5" size={18} data-id="element-19" />
                <p className="text-gray-300 text-sm" data-id="element-20">
                  <span className="text-white font-bold" data-id="element-21">
                    Sistema Faceless Cash + 3 Bonos Gratis
                  </span>
                </p>
              </div>
              <div className="flex items-start gap-3" data-id="element-22">
                <Check className="text-[#00e676] shrink-0 mt-0.5" size={18} data-id="element-23" />
                <p className="text-gray-300 text-sm" data-id="element-24">
                  <span className="text-white font-bold" data-id="element-25">
                    USD Cash-Flow: Nichos de Alta Retención
                  </span>{' '}
                  — Los 5 nichos que pagan entre $15-$40 USD por cada mil vistas
                </p>
              </div>
              <div className="flex items-start gap-3" data-id="element-26">
                <Check className="text-[#00e676] shrink-0 mt-0.5" size={18} data-id="element-27" />
                <p className="text-gray-300 text-sm" data-id="element-28">
                  <span className="text-white font-bold" data-id="element-29">
                    SDR: Scripting de Respuesta Directa
                  </span>{' '}
                  — 20 plantillas de guiones + 10 Hooks de Venta Invisible
                </p>
              </div>
            </div>

            {/* Pricing Block */}
            <div className="text-center pt-2" data-id="element-30">
              <div className="flex items-center justify-center gap-3 mb-1" data-id="element-31">
                <span className="text-gray-500 line-through text-lg font-medium" data-id="element-32">
                  $24.36 USD
                </span>
                <span className="text-[#00e676] text-4xl font-black" data-id="element-33">
                  $16.98 USD
                </span>
              </div>
              <p className="text-[#f5a623] text-sm font-medium mb-2" data-id="element-34">
                Precio especial solo disponible en esta pantalla
              </p>
              <div className="flex items-center justify-center gap-1.5 text-gray-400 text-xs" data-id="element-35">
                <Lock size={12} data-id="element-36" />
                <span data-id="element-37">Pago 100% Seguro</span>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="space-y-4 pt-2" data-id="element-38">
              <motion.button animate={pulseAnimation} onClick={onAccept} className="w-full bg-[#00e676] hover:bg-[#00c853] text-black font-black text-lg md:text-xl py-4 px-6 rounded-xl shadow-[0_0_30px_rgba(0,230,118,0.4)] transition-all transform hover:-translate-y-1 border-b-4 border-[#00b248] cursor-pointer flex flex-col items-center justify-center leading-tight" data-id="element-39">
                <span data-id="element-40">¡SÍ! QUIERO EL PAQUETE COMPLETO</span>
                <span className="text-sm font-bold opacity-80 mt-0.5" data-id="element-41">
                  POR SOLO $16.98 USD
                </span>
              </motion.button>

              <button onClick={onDecline} className="w-full text-gray-500 hover:text-gray-300 text-sm font-medium transition-colors underline underline-offset-4 py-2" data-id="element-42">
                No gracias, continuar sin los extras →
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>;
}